import type { API_ERROR_SERVICE } from './schema';

/**
 * Opaque, display-ready message. NOT a plain string on purpose: a branded string
 * (`string & Brand`) is still assignable to `string`, so it would still compile when
 * passed back into `i18n.t()` — defeating the point. Only a non-string shape blocks
 * that at compile time.
 */
export interface UserFacingMessage {
	readonly __brand: 'UserFacingMessage';
	readonly text: string;
}

/** Typed tagging metadata for the wire envelope's `service` field (Sentry tags only). */
export type ApiErrorService = (typeof API_ERROR_SERVICE)[keyof typeof API_ERROR_SERVICE];

export const ERROR_RESOLUTION = {
	ContractKey: 'contract-key', // stage 1, exact whitelisted key -> translated
	ContractKeyFallback: 'contract-key-fallback', // stage 1, ancestor (dominio[.recurso]) whitelisted -> translated (ADR-2)
	ContractViolation: 'contract-violation', // stage 1, NOT whitelisted -> generic + escalate
	LegacyKey: 'legacy-key', // stage 2, whitelisted -> translated
	LegacyUnresolved: 'legacy-unresolved', // stage 2, not whitelisted -> fell through
	StatusFallback: 'status-fallback', // stage 3, http_{status}
	Generic: 'generic',
	Network: 'network',
	Timeout: 'timeout',
	NonAxios: 'non-axios',
} as const;

export type ErrorResolution = (typeof ERROR_RESOLUTION)[keyof typeof ERROR_RESOLUTION];

export interface ResolvedApiError {
	readonly message: UserFacingMessage;
	readonly status?: number;
	/**
	 * Raw dev detail — Sentry-only by default. May be interpolated into `message` for an
	 * explicit per-key allow-list (`detailPolicy.ts`, D1); never rendered otherwise.
	 */
	readonly detail?: unknown;
	/** Candidate key as sent by the backend, before whitelist resolution. */
	readonly key?: string;
	readonly resolution: ErrorResolution;
}

export interface UnifiedErrorEnvelope {
	readonly resolved: ResolvedApiError;
	readonly reported: boolean;
}

/** Internal factory — the only place a `UserFacingMessage` is constructed. */
export const toUserFacingMessage = (text: string): UserFacingMessage => ({
	__brand: 'UserFacingMessage',
	text,
});
